import React from "react";
import {
  Dialog,
  Typography,
  DialogContent,
  Slide,
  IconButton,
  TextField,
  FormLabel,
  FormControl,
  List,
  ListItem,
  Divider,
  ListItemButton,
  Box,
  useTheme,
  useMediaQuery,
  useColorScheme 
} from "@mui/material";
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import CloseIcon from '@mui/icons-material/Close';
import { TransitionProps } from "@mui/material/transitions";
import { Pedido } from "../pages/Historico/Historico.tsx";

interface OrderDetailsAlertProps {
  open: boolean;
  onClose: () => void;
  pedido: Pedido;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const OrderDetailsAlert = ({ open, onClose, pedido }: OrderDetailsAlertProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scheme = useColorScheme();
  const isLightMode = scheme.mode === 'light';
  const contrastColor = isLightMode ? '#000' : '#fff';

  const formatarDataHora = (data: string, hora: string) => {
    const dataHora = new Date(`${data}T${hora}`);
    return dataHora.toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  const calcularTotalPedido = () => {
    return pedido.itens.reduce((total, item) => total + item.qtd * item.prc, 0);
  };
  
  return (
    <Dialog
      open={open}
      scroll="paper"
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      aria-describedby="alert-dialog-slide-description"
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          [theme.breakpoints.down('sm')]: {
            margin: 0,
            maxHeight: '100vh',
          }
        }
      }}
    >
      {/* Header */}
      <Box 
        display="flex" 
        flexDirection="column"
        padding={isMobile ? 2 : 3}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{
            overflow: 'hidden',
          }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              fontWeight="bold"
              sx={{
                wordBreak: 'break-word',
              }}
            >
              Pedido {formatarDataHora(pedido.capa.data, pedido.capa.hora)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {formatarDataHora(pedido.capa.data, pedido.capa.hora)} | Pedido Nº: {pedido.capa.coD_PEDIDO}
            </Typography>
          </Box>

          <Box display="flex" alignItems="flex-start">
            <Box sx={{ 
              bgcolor: "#6fbf73", 
              color: "#fff", 
              px: theme => theme.spacing(isMobile ? 0.5 : 1),
              py: 0.5, 
              fontSize: isMobile ? 10 : 12, 
              fontWeight: "bold",
              width: "max-content", 
              minWidth: "max-content", 
              borderRadius: 1,
              whiteSpace: "nowrap", 
            }}>
              Status: Enviado
            </Box>

            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ 
                color: contrastColor,
                ml: 1,
                mt: isMobile ? 0 : -1
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box 
          mt={1.5} 
          display="flex" 
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="space-between" 
          alignItems={isMobile ? "flex-start" : "center"}
          gap={isMobile ? 2 : 0}
        >
          <FormControl sx={{ width: isMobile ? '100%' : 'auto' }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <FormLabel htmlFor="observacao">Observação do pedido:</FormLabel>
            </Box>
            <TextField 
              disabled
              inputProps={{ maxLength: 50 }} 
              multiline 
              rows={2}
              sx={{
                width: isMobile ? '100%' : 280,
              }}
              placeholder="nenhuma observação."
              value={pedido.capa.obs || ''} 
            />
          </FormControl>

          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{ 
              whiteSpace: 'nowrap', 
              alignSelf: isMobile ? 'flex-start' : 'flex-end',
              mt: isMobile ? 1 : 0,
            }}
          >
            Total: R$ {calcularTotalPedido().toFixed(2)}
          </Typography>
        </Box>
      </Box>

      {/* Body */}
      <DialogContent 
        id="alert-dialog-slide-description" 
        component="div"
        sx={{
          paddingX: isMobile ? 2 : 3,
          overflow: 'auto',
        }}
      >
        <List sx={{ padding: 0 }}>
          {pedido.itens.map((item, i) => (
            <React.Fragment key={i}>
              <Divider />
              <ListItem sx={{ padding: 0 }}>
                <ListItemButton
                  component="a"
                  sx={{
                    borderRadius: 2,
                    '&:hover': { backgroundColor: 'action.hover', transform: 'scale(1.01)' },
                    transition: 'transform 0.2s ease',
                    padding: isMobile ? '8px 4px' : '8px 16px',
                  }}
                >
                  <Inventory2Rounded color="primary" fontSize={isMobile ? "small" : "large"} />
                  <Box flexGrow={1} sx={{ marginLeft: isMobile ? 1 : 2, overflow: 'hidden' }}>
                    <Typography 
                      variant={isMobile ? "body1" : "subtitle1"} 
                      fontWeight="bold" 
                      color="textPrimary"
                      sx={{
                        wordBreak: 'break-word',
                      }}
                    >
                      {item.proD_REF_FORN} - {item.proD_DESCRICAO}
                    </Typography>
                    <Typography variant={isMobile ? "body2" : "body1"} color="text.secondary" fontWeight="bold">
                      R$ {item.prc.toFixed(2)} x {item.qtd}
                    </Typography>
                    <Typography variant={isMobile ? "body2" : "body1"} color="success.main" fontWeight="bold">
                      Total: R$ {(item.prc * item.qtd).toFixed(2)}
                    </Typography>
                  </Box>
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetailsAlert;